import {
  format,
  parse,
  isValid,
  startOfDay,
  endOfDay,
  isBefore,
  isAfter,
  isSameDay,
} from "date-fns";

/** Default display/parse format — matches legacy MUI `inputFormat="dd/MM/yyyy"`. */
export const DEFAULT_DATE_FORMAT = "dd/MM/yyyy";

export function formatDateValue(date, fmt = DEFAULT_DATE_FORMAT) {
  if (!date || !isValid(date)) return "";
  return format(date, fmt);
}

/**
 * True when `text` has as many characters as a fully formatted value.
 * Incomplete years like `01/02/1` must not be treated as committed dates.
 */
export function isCompleteDateInput(text, fmt = DEFAULT_DATE_FORMAT) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return false;
  if (fmt === DEFAULT_DATE_FORMAT) {
    return /^\d{2}\/\d{2}\/\d{4}$/.test(trimmed);
  }
  return trimmed.length >= format(new Date(2000, 0, 1), fmt).length;
}

export function parseDateInput(text, fmt = DEFAULT_DATE_FORMAT) {
  if (!text || !String(text).trim()) return null;
  const trimmed = String(text).trim();
  const parsed = parse(trimmed, fmt, new Date());
  if (!isValid(parsed)) return undefined;
  // date-fns accepts 1–3 year digits and pads them (`01/02/1` → year 1).
  // Only accept values that already match the formatted date.
  if (format(parsed, fmt) !== trimmed) return undefined;
  return parsed;
}

/** Resolve min/max from DS props and MUI-compat aliases. */
export function resolveDateBounds({
  min,
  max,
  minDate,
  maxDate,
  disablePast,
  disableFuture,
}) {
  const todayStart = startOfDay(new Date());
  const todayEnd = endOfDay(new Date());
  let fromDate = min ?? minDate ?? null;
  let toDate = max ?? maxDate ?? null;

  if (disablePast) {
    fromDate = fromDate && isAfter(fromDate, todayStart) ? fromDate : todayStart;
  }
  if (disableFuture) {
    toDate = toDate && isBefore(toDate, todayEnd) ? toDate : todayEnd;
  }

  return { fromDate, toDate };
}

/**
 * Validate a date against bounds. Returns MUI-compatible error codes or null.
 * @see https://mui.com/x/react-date-pickers/validation/
 */
export function validateDate(date, { fromDate, toDate }) {
  if (!date) return null;
  if (!isValid(date)) return "invalidDate";

  const day = startOfDay(date);

  if (fromDate && isBefore(day, startOfDay(fromDate))) {
    return disablePastError(fromDate) ? "disablePast" : "minDate";
  }
  if (toDate && isAfter(day, startOfDay(toDate))) {
    return disableFutureError(toDate) ? "disableFuture" : "maxDate";
  }
  return null;
}

function disablePastError(fromDate) {
  return isSameDay(fromDate, startOfDay(new Date())) || isAfter(fromDate, startOfDay(new Date()));
}

function disableFutureError(toDate) {
  return isSameDay(toDate, startOfDay(new Date())) || isBefore(toDate, startOfDay(new Date()));
}
